<template>
  <v-container class="vpd-content" fluid>
    <v-overlay :value="overlay" z-index="1000">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <div v-if="!isOpten">
      <vpd-welcome-text :isOpten="isOpten" />
      <vpd-hero-image />
    </div>

    <div v-if="isOpten" class="login-view-container">
      <vpd-welcome-text :isOpten="isOpten" />
      <img
        class="dyc-image"
        alt="DYC logo"
        srcset="
          @/assets/dyc_footprint_white-small.png 220w,
          @/assets/dyc_footprint_white-normal.png 440w,
          @/assets/dyc_footprint_white-large.png 880w
        "
      />
    </div>

    <!-- 🔹 Step 1: normal login panel -->
    <vpd-login-panel
      v-if="!requireOtp"
      @reset-pass="$router.push({ name: 'resetPass' })"
      @signup="$router.push({ name: 'registration' })"
      @submit="submitLogin"
      @federation="handleFederationSignIn"
      ref="loginPanel"
    />

    <!-- 🔹 Step 2: OTP verification -->
    <vpd-confirm-otp-panel
      v-else
      @submit="submitOtp"
      ref="otpPanel"
    />
  </v-container>
</template>

<script>
import VpdLoginPanel from "@/components/auth/vpd-login-panel/vpd-login-panel";
import VpdConfirmOtpPanel from "@/components/auth/vpd-confirm-otp-panel/vpd-confirm-otp-panel";
import VpdWelcomeText from "@/components/vpd-welcome-text/vpd-welcome-text";
import VpdHeroImage from "@/components/auth/vpd-hero-image/vpd-hero-image";
import { signIn, confirmSignIn, fetchAuthSession } from "@aws-amplify/auth";
import { signOut } from "../modules/auth";
import { AmplifyEventBus } from "aws-amplify-vue";
import { camelCase } from "change-case";
import VpdEventBus from "@/modules/vpd-event-bus";
import { resendSignUp } from "@/modules/auth";

const errorMessages = {
  UserNotConfirmedException: "A felhasználói fiók még nem lett aktiválva!",
  PasswordResetRequiredException: "A felhasználói jelszó beállítására van szükség!",
  NotAuthorizedException: "Érvénytelen felhasználónév vagy jelszó!",
  UserNotFoundException: "Hibás vagy nem létező felhasználó azonosító!",
  success: "Sikeres belépés!",
};

const lockoutErrorMessage = "Zárolt felhasználó.";

export default {
  name: "login",
  components: {
    VpdHeroImage,
    VpdLoginPanel,
    VpdConfirmOtpPanel,
    VpdWelcomeText,
  },
  data: () => ({
    overlay: false,
    isOpten: false,
    requireOtp: false,     // ✅ controls which panel is shown
    pendingUsername: null, // ✅ keep the username for OTP step
  }),
  methods: {
    async submitLogin(email, password) {
      try{
        await signOut();
      }
      catch (err) {
      }
      const panel = this.$refs.loginPanel;
      this.overlay = true;

      try {
        const { isSignedIn, nextStep, user } = await signIn({ username: email, password });

        if (isSignedIn) {
          this.finishLogin(user);
          return;
        }

        if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
          this.requireOtp = true;
          this.pendingUsername = email;
          return;
        }

        panel.showAlert("Ismeretlen bejelentkezési állapot!", "error");
      } catch (err) {
        console.error("Sign-in error:", err);
        const handler = `${camelCase(err.name)}Handler`;
        if (typeof this[handler] === "function") this[handler](panel, email);
        else if (err.message?.includes("lockout")) panel.showAlert(lockoutErrorMessage, "error");
        else panel.showAlert(`Sikertelen bejelentkezés! ${errorMessages[err.code]}`, "error");
      } finally {
        this.overlay = false;
      }
    },

    async submitOtp(code) {
      const panel = this.$refs.otpPanel;
      this.overlay = true;

      try {
        // ✅ Confirm OTP with same session still in memory
        const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

        if (isSignedIn) {
          await fetchAuthSession(); // refresh tokens
          AmplifyEventBus.$emit("authState", "signedIn");
          this.finishLogin();
        } else {
          panel.showAlert("További azonosítás szükséges!", "warning");
        }
      } catch (error) {
        console.error("❌ OTP error:", error);
        panel.showAlert("Érvénytelen vagy lejárt kód!", "error");
      } finally {
        this.overlay = false;
      }
    },

    async finishLogin(user) {
      this.requireOtp = false;
      this.overlay = false;

      // store Opten state
      if (user?.attributes?.website === "opten") {
        this.isOpten = true;
        localStorage.setItem("isOpten", true);
      } else {
        this.isOpten = false;
        localStorage.setItem("isOpten", false);
      }

      window.dispatchEvent(
        new CustomEvent("opten-localstorage-changed", {
          detail: { storage: localStorage.getItem("isOpten") },
        })
      );

      AmplifyEventBus.$emit("authState", "signedIn");
    },

    async userNotConfirmedExceptionHandler(panel, email) {
      const signup = await resendSignUp(email);
      if (signup.code !== "success") {
        panel.showAlert("Az érvényesítő kód újraküldése nem sikerült!", "error");
      }
    },

    notAuthorizedExceptionHandler(panel) {
      panel.showAlert(errorMessages.NotAuthorizedException, "error");
    },
    userNotFoundExceptionHandler(panel) {
      panel.showAlert(errorMessages.UserNotFoundException, "error");
    },
    passwordResetRequiredExceptionHandler() {
      this.$router.push({ name: "newPass" });
    },
    handleFederationSignIn(provider) {
      VpdEventBus.$emit("redirect", { name: "federated", payload: provider });
    },
  },
  created() {
    this.isOpten = localStorage.isOpten ? JSON.parse(localStorage.isOpten) : false;
  },
};
</script>
